import clsx from 'clsx';
import { Info, Lightbulb } from 'lucide-react';

const TONE_STYLES = {
  default: 'bg-slate-800/70 border-slate-700/80',
  info:    'bg-blue-500/8 border-blue-500/25',
  amber:   'bg-amber-500/8 border-amber-500/25',
  emerald: 'bg-emerald-500/8 border-emerald-500/25',
};

/**
 * Prominent on-screen explanation for charts and data panels.
 * Use below titles or under charts — readable at a glance, not tiny footnotes.
 */
export default function ChartDataCallout({
  title,
  children,
  tone = 'default',
  icon: Icon = Info,
  className,
}) {
  return (
    <div
      className={clsx(
        'rounded-xl border px-4 py-3',
        TONE_STYLES[tone] || TONE_STYLES.default,
        className,
      )}
      role="note"
    >
      <div className="flex gap-3">
        <Icon size={18} className="shrink-0 mt-0.5 text-slate-400" aria-hidden />
        <div className="min-w-0 text-sm leading-relaxed text-slate-300">
          {title && (
            <p className="font-semibold text-slate-100 mb-1.5">{title}</p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

export function ChartDataCalloutList({ items, className }) {
  if (!items?.length) return null;
  return (
    <ul className={clsx('mt-2 space-y-1.5 text-slate-400', className)}>
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-slate-600 shrink-0">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Human-readable percentile copy for district price distribution. */
export function districtPercentileExplanation(percentile, median, district) {
  if (percentile == null) return null;
  const area = district || 'this district';
  const med = median
    ? ` (typical sale in ${area}: about £${Math.round(median / 1000)}k)`
    : '';

  if (percentile >= 85) {
    return {
      tone: 'amber',
      title: 'Priced toward the top of the district',
      body: `This asking price is higher than ${percentile}% of all homes sold in ${area} in the last 3 years${med}. That often means a premium or larger home — flats and smaller houses pull the district average down. Compare same-type sales, not this chart alone.`,
    };
  }
  if (percentile >= 70) {
    return {
      tone: 'amber',
      title: 'Above typical district sales',
      body: `Higher than ${percentile}% of recent sales in ${area}${med}. Useful context, not a valuation of this specific property.`,
    };
  }
  if (percentile <= 25) {
    return {
      tone: 'emerald',
      title: 'Below most recent district sales',
      body: `Lower than ${percentile}% of sales in ${area} in the last 3 years${med}. Could indicate value — or a smaller / different property type.`,
    };
  }
  return {
    tone: 'info',
    title: 'Mid-range for the district',
    body: `Around the ${percentile}th percentile of all sales in ${area} in the last 3 years${med}. This compares asking price to every property type sold locally.`,
  };
}

export { Lightbulb };
